/**
 * Learn About My Body — First Chapter
 * Routes: /demo/cycle/learn  ·  /demo/first-chapter/learn
 *
 * PageHeader (title, pill, CTA) is rendered by FertilityDemoSection.
 * This page supplies the phase map, topic tabs, and learn cards.
 */

import { useState, useCallback } from 'react';
import { useApp } from '../../context/AppContext';
import { useMediaQuery } from '../../hooks/useMediaQuery';
import { introCardKeyline, tabNavContainer, tabNavItemColored } from '../../designSystem';
import NovaAvatarImg from '../../components/NovaAvatarImg';

const FC = {
  accent: '#7F77DD',
  accentDark: '#534AB7',
  accentDeep: '#3C3489',
  accentSoft: '#EEEDFE',
  accentBorder: 'rgba(127, 119, 221, 0.53)',
  gradStart: '#EEEDFE',
  gradEnd: '#E1F5EE',
  pageBg: '#FFFCF9',
  cardBg: '#FFFDF9',
  ink: '#111111',
  body: '#555555',
  muted: '#888888',
  border: '#E8E0D8',
};

const PHASES = [
  {
    id: 'menstrual',
    name: 'Period',
    days: 'days 1–5',
    color: '#F4C0D1',
    textColor: '#72243E',
    description:
      'Bleeding phase. Prostaglandins cause cramping. Energy is lower.',
  },
  {
    id: 'follicular',
    name: 'Follicular',
    days: 'days 6–11',
    color: '#E8D5F5',
    textColor: '#534AB7',
    description:
      'Estrogen rises. Energy and mood often lift. Skin can clear up.',
  },
  {
    id: 'ovulation',
    name: 'Ovulation',
    days: 'days 12–16',
    color: '#EEEDFE',
    textColor: '#3C3489',
    description:
      'An egg is released. Energy peaks. Discharge changes. Some feel a twinge.',
  },
  {
    id: 'luteal',
    name: 'Luteal',
    days: 'days 17–28',
    color: '#E1F5EE',
    textColor: '#085041',
    description:
      'Progesterone rises then drops. PMS symptoms can appear. Body prepares for period.',
  },
];

const LEARN_CONTENT = {
  ovulation: [
    {
      q: 'What actually happens during ovulation?',
      a: "One of your ovaries releases a mature egg into your fallopian tube. The egg only survives 12–24 hours. This is the most fertile point in your cycle. You might feel more energetic than usual — that's normal and tied to the estrogen peak that triggers ovulation.",
      tag: 'Cycle phases',
      emoji: '🥚',
    },
    {
      q: 'Why does my discharge change mid-cycle?',
      a: "Clear, stretchy discharge that looks like egg whites is your body's signal that ovulation is approaching or happening. It's completely healthy and actually helps protect the reproductive tract. If it's yellow, grey, or has an unusual smell, that's worth mentioning to a doctor.",
      tag: 'Body signals',
      emoji: '💧',
    },
    {
      q: 'Why do I feel so good right now?',
      a: "Estrogen peaks around ovulation — this hormonal high is often when people feel most confident, social, and energetic. Your body is literally at its most primed. Some people notice their voice sounds slightly different, their skin is clearer, or they're more talkative. All real effects.",
      tag: 'Hormones',
      emoji: '⚡',
    },
    {
      q: 'Is the twinge on one side normal?',
      a: "Yes — it has a name: Mittelschmerz, which is German for 'middle pain.' It's a brief cramp or twinge on one side of your lower abdomen when the egg releases. It lasts anywhere from a few minutes to a day or two. If it's severe or lasts longer, mention it to a doctor.",
      tag: 'Body signals',
      emoji: '😬',
    },
  ],
  basics: [
    {
      q: "What's a normal period?",
      a: "Most periods last 3–7 days and happen every 21–35 days. Cycles are often irregular in the first 1–2 years — that's normal. If you've had periods for more than 2 years and they're still very unpredictable, it's worth chatting with a doctor.",
      tag: 'Basics',
      emoji: '📅',
    },
    {
      q: 'Why do cramps happen?',
      a: "Your uterus is a muscle. It contracts to push out the lining — that's cramping. Mild to moderate cramping is completely normal. Pain that stops you from going to school, sleeping, or doing things you want to do is not something you should just live with. That's treatable.",
      tag: 'Cramps',
      emoji: '😤',
    },
    {
      q: "What does 'normal' flow look like?",
      a: "Light to moderate flow — 3–6 tablespoons total over your whole period. Clots smaller than a 10p coin are normal. Soaking more than one pad or tampon per hour for several hours in a row is worth talking to a doctor about — that's heavier than typical.",
      tag: 'Flow',
      emoji: '🩸',
    },
    {
      q: 'What is PMS and why does it happen?',
      a: "PMS stands for premenstrual syndrome — mood shifts, irritability, bloating, or sadness that show up 1–2 weeks before your period. It's caused by progesterone rising and then dropping sharply. It's very common. If it's severe or feels like more than moodiness, that's also treatable (it might be PMDD).",
      tag: 'Mood',
      emoji: '🌀',
    },
  ],
  mood: [
    {
      q: 'Why do I feel depressed before my period?',
      a: "Progesterone drops sharply in the days before your period starts, which can cause low mood, anxiety, or even crying without knowing why. This is real, measurable, and tied directly to your hormones — not a character flaw. Tracking it alongside your cycle helps you predict when it will hit.",
      tag: 'Mood',
      emoji: '😔',
    },
    {
      q: 'Is it normal to feel anxious mid-cycle?',
      a: "Some people feel a dip in mood or a burst of anxiety around ovulation despite the estrogen peak. If you track your moods alongside your cycle, you'll start to see your personal pattern — which is different from everyone else's.",
      tag: 'Mood',
      emoji: '😰',
    },
    {
      q: 'Why do I snap at people before my period?',
      a: "Irritability in the luteal phase (days 17–28 of a typical cycle) is one of the most common PMS symptoms. The hormonal shift genuinely affects your nervous system's response to stress. Knowing it's coming makes it more manageable — and lets you be kinder to yourself about it.",
      tag: 'Mood',
      emoji: '😤',
    },
  ],
  hormones: [
    {
      q: 'What do my hormones actually do?',
      a: 'Four main hormones run your cycle: estrogen (builds the uterine lining, lifts mood), progesterone (prepares the uterus in case of pregnancy, then drops to trigger your period), LH (triggers ovulation), and FSH (matures eggs). They rise and fall in a predictable rhythm every cycle.',
      tag: 'Hormones',
      emoji: '🧬',
    },
    {
      q: 'Why do hormones affect my skin?',
      a: "Testosterone (yes, everyone has some) stimulates oil production in skin. It's relatively higher during the luteal phase (before your period), which is why breakouts often cluster there. Estrogen tends to suppress this — which is why skin often clears up mid-cycle.",
      tag: 'Hormones',
      emoji: '✨',
    },
  ],
  doctor: [
    {
      q: 'When should I actually see a doctor?',
      a: "Heavy flow (soaking through protection hourly for hours), severe cramps that stop you doing things, periods that stop for 3+ months, cycles shorter than 21 days or longer than 45 days, spotting between periods, or any symptom that worries you. You don't need to wait until it's unbearable.",
      tag: 'Health',
      emoji: '🏥',
    },
    {
      q: 'What will a doctor actually ask me?',
      a: "When your period started, how long it lasts, how heavy it is, whether you have pain, and your general health. Tracking in your period log here means you'll have real data to share — which makes the appointment much more useful.",
      tag: 'Health',
      emoji: '📋',
    },
  ],
};

const TOPIC_TABS = [
  { id: 'ovulation', label: 'This phase', emoji: '🥚' },
  { id: 'basics', label: 'Basics', emoji: '🩸' },
  { id: 'mood', label: 'Mood', emoji: '🌀' },
  { id: 'hormones', label: 'Hormones', emoji: '🧬' },
  { id: 'doctor', label: 'When to see a doctor', emoji: '🏥' },
];

/** Two-column detail copy for the milestone-style “Your cycle right now” block (mirrors Fertility My Journey treatment milestones). */
const PHASE_INSIGHTS = {
  menstrual: {
    expect: [
      { emoji: '🩸', title: 'Shedding the lining', sub: 'Bleeding is your uterus resetting — cramps come from prostaglandins helping it contract.' },
      { emoji: '😴', title: 'Lower energy is common', sub: 'Iron loss and hormones can make you tired the first few days.' },
    ],
    watch: [
      { emoji: '📝', title: 'Log flow & pain', sub: 'You’ll spot what’s normal for you — useful if you ever see a doctor.' },
      { emoji: '🔥', title: 'Heat & rest help', sub: 'Warmth on your lower belly relaxes the muscle that’s cramping.' },
    ],
  },
  follicular: {
    expect: [
      { emoji: '🌱', title: 'Estrogen climbs', sub: 'Many people feel more energy and a brighter mood in this stretch.' },
      { emoji: '✨', title: 'Skin & focus often improve', sub: 'Hormone shifts can show up in how you look and feel day to day.' },
    ],
    watch: [
      { emoji: '📅', title: 'Good time to learn patterns', sub: 'Notice what helps when you feel good — it’s data for harder days too.' },
      { emoji: '💬', title: 'Ask Nova anything basic', sub: 'No question is too small; understanding reduces the “is something wrong?” spiral.' },
    ],
  },
  ovulation: {
    expect: [
      { emoji: '🥚', title: 'An egg releases', sub: 'One ovary sends an egg into the tube — the most fertile days are around now.' },
      { emoji: '⚡', title: 'Energy often peaks', sub: 'Estrogen is high; many people feel their most confident or social now.' },
    ],
    watch: [
      { emoji: '💧', title: 'Discharge can change', sub: 'Clear, stretchy mucus is a normal mid-cycle sign.' },
      { emoji: '😬', title: 'One-sided twinge?', sub: 'Brief Mittelschmerz is common; severe or lasting pain is worth mentioning.' },
    ],
  },
  luteal: {
    expect: [
      { emoji: '🌙', title: 'Progesterone rises', sub: 'Your body prepares for a possible period; bloating or breast tenderness can show up.' },
      { emoji: '🌀', title: 'Mood can shift', sub: 'If PMS hits, it’s hormonal — not a flaw in your personality.' },
    ],
    watch: [
      { emoji: '🧠', title: 'Track mood with your cycle', sub: 'Patterns make tough days easier to predict and kinder to yourself.' },
      { emoji: '🏥', title: 'Heavy symptoms?', sub: 'You deserve care if pain or mood swings disrupt your life.' },
    ],
  },
};

function getCurrentPhase(cycleDay, cycleLength = 28) {
  const ovDay = cycleLength - 14;
  if (cycleDay <= 5) return 'menstrual';
  if (cycleDay <= ovDay - 3) return 'follicular';
  if (cycleDay <= ovDay + 2) return 'ovulation';
  return 'luteal';
}

function phaseToTab(phaseId) {
  if (phaseId === 'menstrual' || phaseId === 'follicular') return 'basics';
  if (phaseId === 'ovulation') return 'ovulation';
  if (phaseId === 'luteal') return 'mood';
  return 'basics';
}

export default function LearnAboutMyBody({ navigate }) {
  const nav = navigate || (() => {});
  const { setNovaDraft } = useApp();
  const m = useMediaQuery('(max-width: 768px)');

  const cycleDay = 14;
  const cycleLength = 28;
  const currentPhaseId = getCurrentPhase(cycleDay, cycleLength);
  const currentPhase = PHASES.find((p) => p.id === currentPhaseId) || PHASES[2];

  const [activeTab, setActiveTab] = useState(() => phaseToTab(currentPhaseId));
  const [phaseStepIndex, setPhaseStepIndex] = useState(() =>
    Math.max(0, PHASES.findIndex((p) => p.id === currentPhaseId))
  );
  const [expandedCard, setExpandedCard] = useState(null);
  const [readItems, setReadItems] = useState(() => new Set());

  const activeContent = LEARN_CONTENT[activeTab] ?? [];
  const phaseAtStep = PHASES[phaseStepIndex] || PHASES[0];
  const insights = PHASE_INSIGHTS[phaseAtStep.id] || PHASE_INSIGHTS.ovulation;

  const markRead = useCallback((q) => {
    setReadItems((prev) => new Set([...prev, q]));
  }, []);

  const openNova = useCallback(
    (text) => {
      if (text && setNovaDraft) setNovaDraft(text);
      nav('/nova');
    },
    [nav, setNovaDraft]
  ); // empty text: navigate only (e.g. Open Nova →)

  const rowMilestone = (item, last, key) => (
    <div
      key={key ?? item.title}
      style={{
        display: 'flex',
        gap: 10,
        alignItems: 'flex-start',
        paddingBottom: 8,
        marginBottom: last ? 0 : 8,
        borderBottom: last ? 'none' : `0.5px solid ${FC.accentBorder}`,
      }}
    >
      <span style={{ fontSize: 16, marginTop: -1, flexShrink: 0 }}>{item.emoji}</span>
      <div>
        <div style={{ fontSize: 12, fontWeight: 600, color: '#2d1a22' }}>{item.title}</div>
        <div style={{ fontSize: 11, color: 'rgba(60,30,45,0.55)' }}>{item.sub}</div>
      </div>
    </div>
  );

  const phaseProgressPct =
    PHASES.length <= 1 ? 0 : (phaseStepIndex / (PHASES.length - 1)) * 100;

  return (
    <div
      style={{
        background: 'transparent',
        fontFamily: "'Plus Jakarta Sans', -apple-system, sans-serif",
        width: '100%',
        boxSizing: 'border-box',
        display: 'flex',
        flexDirection: 'column',
        gap: 16,
      }}
    >
      {/* Status card — gradient, chapter-colored */}
      <div
        style={{
          background: `linear-gradient(140deg, ${FC.gradStart}, ${FC.gradEnd})`,
          border: `0.5px solid ${FC.accentBorder}`,
          borderRadius: 16,
          padding: '20px 24px',
          boxShadow: '0 2px 16px rgba(17, 17, 17, 0.06)',
        }}
      >
        <p
          style={{
            fontSize: 10,
            fontWeight: 700,
            letterSpacing: '0.12em',
            color: FC.accentDark,
            marginBottom: 8,
            textTransform: 'uppercase',
          }}
        >
          FIRST CHAPTER · LEARN
        </p>
        <h2
          style={{
            fontSize: 22,
            fontWeight: 700,
            color: FC.ink,
            fontFamily: "'Playfair Display', serif",
            marginBottom: 6,
          }}
        >
          Your body is doing something specific today.
        </h2>
        <p
          style={{
            fontSize: 13,
            color: FC.body,
            marginBottom: 12,
            lineHeight: 1.5,
          }}
        >
          You&apos;re on day {cycleDay} — the {currentPhase.name} phase. {currentPhase.description}
        </p>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {[`${currentPhase.name} phase`, `Day ${cycleDay}`, `${cycleLength}-day cycle`].map((label) => (
            <span
              key={label}
              style={{
                background: 'rgba(255,255,255,0.70)',
                border: `0.5px solid ${FC.accentBorder}`,
                borderRadius: 999,
                padding: '4px 12px',
                fontSize: 11,
                fontWeight: 500,
                color: FC.accentDark,
              }}
            >
              {label}
            </span>
          ))}
        </div>
      </div>

      {/* Nova strip — floating card on page background */}
      <div
        style={{
          background: FC.cardBg,
          border: `0.5px solid ${FC.border}`,
          borderRadius: 16,
          padding: '12px 16px',
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: 12,
          boxShadow: '0 2px 16px rgba(17, 17, 17, 0.04)',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
          <NovaAvatarImg chapter="ttc" size={32} />
          <div>
            <p
              style={{
                fontSize: 11,
                fontWeight: 600,
                color: FC.accent,
                marginBottom: 3,
              }}
            >
              Nova · learning
            </p>
            <p style={{ fontSize: 13, color: FC.body, lineHeight: 1.5, margin: 0 }}>
              You&apos;re in the {currentPhase.name.toLowerCase()} phase — good timing to learn what that actually means.
              There&apos;s no such thing as a question that&apos;s too basic.
            </p>
          </div>
        </div>
        <button
          type="button"
          onClick={() => openNova('')}
          style={{
            fontSize: 13,
            color: FC.accent,
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            fontWeight: 500,
            flexShrink: 0,
            whiteSpace: 'nowrap',
          }}
        >
          Open Nova →
        </button>
      </div>

      {/* Your cycle right now — same structure as Fertility My Journey “Treatment milestones” */}
      <div
        style={{
          background: `linear-gradient(140deg, ${FC.gradStart}, ${FC.gradEnd})`,
          border: `0.5px solid ${FC.accentBorder}`,
          borderRadius: 22,
          boxShadow: introCardKeyline.boxShadow,
          padding: '22px 26px',
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14, flexWrap: 'wrap', gap: 8 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: '#2d1a22' }}>Your cycle right now</div>
          <div style={{ fontSize: 11, color: 'rgba(60,30,45,0.4)' }}>
            Step {phaseStepIndex + 1} of {PHASES.length} · {cycleLength}-day cycle · Day {cycleDay}
          </div>
        </div>

        <div style={{ display: 'flex', position: 'relative', marginBottom: 16 }}>
          <div
            style={{
              position: 'absolute',
              top: 18,
              left: 18,
              right: 18,
              height: 2,
              background: 'rgba(127, 119, 221, 0.15)',
            }}
          />
          <div
            style={{
              position: 'absolute',
              top: 18,
              left: 18,
              height: 2,
              background: FC.accent,
              width: `${phaseProgressPct}%`,
              transition: 'width 0.35s ease',
            }}
          />
          {PHASES.map((phase, i) => {
            const done = i < phaseStepIndex;
            const isHere = i === phaseStepIndex;
            return (
              <button
                key={phase.id}
                type="button"
                onClick={() => {
                  setPhaseStepIndex(i);
                  setActiveTab(phaseToTab(phase.id));
                }}
                style={{
                  flex: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 8,
                  zIndex: 1,
                  background: 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                  padding: 0,
                  font: 'inherit',
                }}
                aria-label={`View ${phase.name} phase`}
              >
                <div
                  style={{
                    width: 36,
                    height: 36,
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 12,
                    fontWeight: 700,
                    background: done ? 'var(--sage-m)' : isHere ? FC.accentSoft : 'rgba(255,255,255,0.55)',
                    border: done
                      ? '2px solid var(--sage)'
                      : isHere
                        ? `2px solid ${FC.accent}`
                        : '2px solid rgba(127,119,221,0.22)',
                    color: done ? '#085041' : isHere ? FC.accentDeep : 'rgba(60,30,45,0.35)',
                    boxShadow: isHere ? `0 0 0 4px ${FC.accentSoft}` : 'none',
                  }}
                >
                  {done ? '✓' : isHere ? '→' : i + 1}
                </div>
                <div
                  style={{
                    fontSize: 10,
                    color: isHere ? FC.accentDark : done ? 'var(--sage-d)' : 'rgba(60,30,45,0.35)',
                    fontWeight: isHere ? 700 : 400,
                    textAlign: 'center',
                    lineHeight: 1.3,
                  }}
                >
                  {phase.name}
                </div>
              </button>
            );
          })}
        </div>

        <div
          style={{
            background: 'rgba(255,255,255,0.68)',
            border: `0.5px solid ${FC.accentBorder}`,
            borderRadius: 12,
            padding: '16px 18px',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, marginBottom: 14, flexWrap: 'wrap' }}>
            <div>
              <div style={{ fontSize: 16, fontWeight: 700, color: '#2d1a22' }}>{phaseAtStep.name}</div>
              <div style={{ fontSize: 12, color: 'rgba(60,30,45,0.5)', marginTop: 2 }}>{phaseAtStep.days}</div>
            </div>
            <button
              type="button"
              onClick={() => openNova(`Tell me more about the ${phaseAtStep.name.toLowerCase()} phase of my cycle`)}
              style={{
                background: FC.accent,
                color: 'white',
                border: 'none',
                borderRadius: 10,
                padding: '10px 18px',
                fontSize: 13,
                fontWeight: 600,
                cursor: 'pointer',
                fontFamily: 'inherit',
              }}
            >
              Ask Nova →
            </button>
          </div>
          <p style={{ fontSize: 12, color: FC.body, lineHeight: 1.6, margin: '0 0 14px' }}>{phaseAtStep.description}</p>
          <div style={{ display: 'grid', gridTemplateColumns: m ? '1fr' : '1fr 1fr', gap: 10 }}>
            <div
              style={{
                background: 'rgba(255,255,255,0.75)',
                border: `0.5px solid ${FC.accentBorder}`,
                borderRadius: 12,
                padding: '14px 16px',
              }}
            >
              <div
                style={{
                  fontSize: 11,
                  fontWeight: 700,
                  color: FC.accent,
                  textTransform: 'uppercase',
                  letterSpacing: '0.08em',
                  marginBottom: 10,
                }}
              >
                What to expect
              </div>
              {insights.expect.map((item, i) => rowMilestone(item, i === insights.expect.length - 1, item.title))}
            </div>
            <div
              style={{
                background: 'rgba(255,255,255,0.75)',
                border: `0.5px solid ${FC.accentBorder}`,
                borderRadius: 12,
                padding: '14px 16px',
              }}
            >
              <div
                style={{
                  fontSize: 11,
                  fontWeight: 700,
                  color: FC.accent,
                  textTransform: 'uppercase',
                  letterSpacing: '0.08em',
                  marginBottom: 10,
                }}
              >
                What to notice
              </div>
              {insights.watch.map((item, i) => rowMilestone(item, i === insights.watch.length - 1, `w-${item.title}`))}
            </div>
          </div>
        </div>
      </div>

      {/* Topic tabs — underline nav (matches product tab pattern) */}
      <div
        style={{
          background: FC.cardBg,
          border: `0.5px solid ${FC.border}`,
          borderRadius: 16,
          padding: '4px 8px 0',
          boxShadow: '0 2px 16px rgba(17, 17, 17, 0.04)',
        }}
      >
        <div
          style={{
            ...tabNavContainer,
            borderBottom: `0.5px solid ${FC.border}`,
            overflowX: 'auto',
            scrollbarWidth: 'none',
          }}
        >
          {TOPIC_TABS.map((tab) => {
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                type="button"
                onClick={() => setActiveTab(tab.id)}
                style={{
                  ...tabNavItemColored(isActive, FC.accent),
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                  flexShrink: 0,
                }}
              >
                <span style={{ fontSize: 16, lineHeight: 1, flexShrink: 0 }} aria-hidden>
                  {tab.emoji}
                </span>
                {tab.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Learn cards */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: m ? '1fr' : 'repeat(2, 1fr)',
          gap: 10,
        }}
      >
        {activeContent.map((item) => {
          const isExpanded = expandedCard === item.q;
          const isRead = readItems.has(item.q);
          return (
            <div
              key={item.q}
              role="button"
              tabIndex={0}
              onClick={() => {
                setExpandedCard(isExpanded ? null : item.q);
                markRead(item.q);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  setExpandedCard(isExpanded ? null : item.q);
                  markRead(item.q);
                }
              }}
              style={{
                border: isExpanded ? `1.5px solid ${FC.accent}` : `0.5px solid ${FC.border}`,
                borderRadius: 12,
                padding: '12px 14px',
                background: isRead && !isExpanded ? '#FAFAFA' : '#FFFFFF',
                cursor: 'pointer',
                opacity: isRead && !isExpanded ? 0.72 : 1,
                transition: 'all 0.15s',
              }}
            >
              <div style={{ fontSize: 20, marginBottom: 8 }}>{item.emoji}</div>
              <p
                style={{
                  fontSize: 12,
                  fontWeight: 600,
                  color: FC.ink,
                  lineHeight: 1.4,
                  marginBottom: 6,
                }}
              >
                {item.q}
              </p>

              {!isExpanded ? (
                <p style={{ fontSize: 11, color: FC.muted, lineHeight: 1.4, margin: 0 }}>
                  {item.a.slice(0, 65)}...
                </p>
              ) : (
                <div>
                  <p style={{ fontSize: 12, color: FC.body, lineHeight: 1.6, marginBottom: 12 }}>{item.a}</p>
                  <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        openNova(`Tell me more about: ${item.q}`);
                      }}
                      style={{
                        fontSize: 11,
                        color: FC.accent,
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        fontWeight: 500,
                        padding: 0,
                      }}
                    >
                      Ask Nova to go deeper →
                    </button>
                    <span style={{ fontSize: 11, color: FC.muted }}>·</span>
                    <span
                      style={{
                        fontSize: 10,
                        background: FC.accentSoft,
                        color: FC.accentDark,
                        padding: '2px 8px',
                        borderRadius: 999,
                        fontWeight: 500,
                      }}
                    >
                      {item.tag}
                    </span>
                  </div>
                </div>
              )}

              {isRead && !isExpanded && (
                <p style={{ fontSize: 10, color: FC.accent, marginTop: 6, fontWeight: 600 }}>✓ Read</p>
              )}
            </div>
          );
        })}
      </div>

      {/* Ask Nova input */}
      <div
        style={{
          background: FC.accentSoft,
          borderRadius: 14,
          padding: '12px 14px',
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          border: `0.5px solid ${FC.accentBorder}`,
        }}
      >
        <NovaAvatarImg chapter="ttc" size={28} />
        <AskNovaInput
          activeTab={activeTab}
          onAsk={(q) => openNova(q)}
        />
      </div>
    </div>
  );
}

function AskNovaInput({ activeTab, onAsk }) {
  const [novaQuery, setNovaQuery] = useState('');
  const tabLabel = TOPIC_TABS.find((t) => t.id === activeTab)?.label || 'anything';

  return (
    <>
      <input
        value={novaQuery}
        onChange={(e) => setNovaQuery(e.target.value)}
        placeholder={`Ask Nova about ${tabLabel.toLowerCase()}...`}
        style={{
          flex: 1,
          background: '#FFFFFF',
          border: `0.5px solid ${FC.accentBorder}`,
          borderRadius: 999,
          padding: '7px 14px',
          fontSize: 12,
          color: FC.body,
          outline: 'none',
          minWidth: 0,
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && novaQuery.trim()) {
            onAsk(novaQuery.trim());
            setNovaQuery('');
          }
        }}
      />
      <button
        type="button"
        onClick={() => {
          if (novaQuery.trim()) {
            onAsk(novaQuery.trim());
            setNovaQuery('');
          }
        }}
        style={{
          background: FC.accent,
          color: '#FFFFFF',
          border: 'none',
          borderRadius: 999,
          padding: '7px 16px',
          fontSize: 12,
          fontWeight: 600,
          cursor: 'pointer',
          flexShrink: 0,
        }}
      >
        Ask
      </button>
    </>
  );
}
